import { motion } from "framer-motion";

const ease = [0.22, 1, 0.36, 1];

/**
 * Numbered chapter block — matches the pattern used in Heritage, Culture,
 * Genealogy and Memorials pages so every article reads consistently.
 */
export function Chapter({ n, title, subtitle, children }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease }}
      className="max-w-[1600px] mx-auto px-6 md:px-10 py-14 md:py-20 border-t border-[color:var(--ink-line)]"
    >
      <div className="grid grid-cols-12 gap-6 md:gap-10 mb-10 md:mb-14 items-end">
        <div className="col-span-12 md:col-span-2">
          <div className="numeric-index text-[color:var(--ink-soft)]">{n}</div>
        </div>
        <div className="col-span-12 md:col-span-10">
          <h2
            className="heritage-title leading-[0.95] tracking-tight"
            style={{ fontSize: "clamp(30px, 4vw, 58px)" }}
          >
            {title}
            {subtitle ? (
              <>
                {" "}
                <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
                  {subtitle}
                </span>
              </>
            ) : null}
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-6 md:gap-10">
        <div className="hidden md:block md:col-span-2" />
        <div className="col-span-12 md:col-span-10">{children}</div>
      </div>
    </motion.section>
  );
}

export function Body({ children }) {
  return (
    <p className="text-[17px] md:text-[19px] leading-[1.65] max-w-[62ch] text-[color:var(--ink)]">
      {children}
    </p>
  );
}

export function Prose({ children }) {
  return <div className="space-y-6 md:space-y-7">{children}</div>;
}

export function Ledger({ items, columns = 2 }) {
  const colClass = columns === 3 ? "md:grid-cols-3" : "md:grid-cols-2";
  return (
    <ul
      className={`mt-2 grid grid-cols-1 ${colClass} gap-x-10 gap-y-0 border-t border-[color:var(--ink-line)]`}
    >
      {items.map((item, i) => (
        <li
          key={i}
          className="flex items-baseline gap-4 py-3 border-b border-[color:var(--ink-line)]"
        >
          <span className="numeric-index text-[color:var(--ink-soft)] w-8 shrink-0">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span
            className="font-serif-display leading-[1.15]"
            style={{ fontSize: "clamp(17px, 1.5vw, 22px)" }}
          >
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Full-width pull quote in italic serif */
export function PullQuote({ children, attribution }) {
  return (
    <figure className="my-6 md:my-10 border-l-2 border-[color:var(--rust)] pl-6 md:pl-8">
      <blockquote
        className="font-italic-serif italic leading-[1.15] text-[color:var(--sepia)]"
        style={{ fontSize: "clamp(24px, 3vw, 40px)" }}
      >
        "{children}"
      </blockquote>
      {attribution && (
        <figcaption className="mt-3 numeric-index text-[color:var(--ink-soft)]">
          — {attribution}
        </figcaption>
      )}
    </figure>
  );
}

/** Editorial "kicker" — the small article meta strip above a chapter */
export function Kicker({ chapter, minutes, dateline }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 numeric-index text-[color:var(--ink-soft)]">
      {chapter && <span>{chapter}</span>}
      {minutes && (
        <>
          <span aria-hidden>·</span>
          <span>{minutes} min read</span>
        </>
      )}
      {dateline && (
        <>
          <span aria-hidden>·</span>
          <span>{dateline}</span>
        </>
      )}
    </div>
  );
}
